import { diffText } from '@/shared/lib/utils/textDiff';

/** Кусок комментария в сравнении с первичной версией. */
export type CommentDiffPiece = {
  kind: 'equal' | 'added' | 'removed';
  text: string;
};

/**
 * Разметка сравнения версий комментария в координатах действующей версии без разметки:
 * какие её участки добавлены и в каких местах убран текст первичной версии.
 */
export type CommentDiffMarkup = {
  added: { start: number; end: number }[];
  removed: { position: number; text: string }[];
  /** Длина действующей версии без разметки — по ней распознаётся её конец */
  length: number;
};

/** Сопоставляет первичную и действующую версии комментария (обе — без разметки). */
export const buildCommentDiffMarkup = (previousPlain: string, nextPlain: string): CommentDiffMarkup => {
  const added: CommentDiffMarkup['added'] = [];
  const removed: CommentDiffMarkup['removed'] = [];
  let position = 0;

  for (const fragment of diffText(previousPlain, nextPlain)) {
    if (fragment.kind === 'removed') {
      removed.push({ position, text: fragment.text });
      continue;
    }
    if (fragment.kind === 'added') added.push({ start: position, end: position + fragment.text.length });
    position += fragment.text.length;
  }

  return { added, removed, length: position };
};

const isAddedAt = (markup: CommentDiffMarkup, position: number): boolean =>
  markup.added.some((range) => position >= range.start && position < range.end);

/**
 * Режет текст одного участка комментария на куски сравнения.
 *
 * `offset` — позиция участка в действующей версии без разметки. Убранный текст выводится в том
 * месте, где он стоял: его позиция принадлежит участку, который с неё начинается, а убранное в
 * самом конце достаётся последнему участку.
 */
export const sliceCommentDiff = (markup: CommentDiffMarkup, text: string, offset: number): CommentDiffPiece[] => {
  const end = offset + text.length;
  const boundaries = new Set<number>([offset, end]);
  for (const range of markup.added) {
    if (range.start > offset && range.start < end) boundaries.add(range.start);
    if (range.end > offset && range.end < end) boundaries.add(range.end);
  }
  for (const mark of markup.removed) {
    if (mark.position > offset && mark.position < end) boundaries.add(mark.position);
  }

  const points = [...boundaries].sort((left, right) => left - right);
  const pieces: CommentDiffPiece[] = [];

  for (const [index, point] of points.entries()) {
    const isLastPointOfComment = point === end && end === markup.length;
    if (point < end || isLastPointOfComment) {
      for (const mark of markup.removed) {
        if (mark.position === point) pieces.push({ kind: 'removed', text: mark.text });
      }
    }

    const next = points[index + 1];
    if (next === undefined || next === point) continue;
    pieces.push({
      kind: isAddedAt(markup, point) ? 'added' : 'equal',
      text: text.slice(point - offset, next - offset),
    });
  }

  return pieces;
};
